import { Box } from "@mui/material";
import { useState } from "react";
import dateformat from "dateformat";
import Upload2LineIcon from "remixicon-react/Upload2LineIcon";
import { Button, Modal, Text, Import } from "$components";
import { useStyles } from "../Stock.styles";
import { DEFAULT_DATE_FORMAT } from "$utils/utils";
import { getFileUrl } from "$api/stockApi";
import logger from "$utils/logger";
import useAuth from "$hooks/useAuth";
import { useJobs } from "$hooks/useJobs";
import { JobType } from "$types/Job";

const IMPORT_CLIENT_INVALID_ERROR_MSG =
  "Le fichier que vous essayez d'importer n'est pas conforme. Veuillez télécharger le template proposé afin d'ajuster vos données.";
const IMPORT_CLIENT_ERROR_MSG =
  "Attention. Le fichier que vous essayez d’importer contient un ou plusieurs champs obligatoires vides (indiqués par un astérisque *). Veuillez apporter les modifications requises et importer de nouveau le fichier.";

export const ModalPatch = ({
  companyLastFile,
  dataImport,
  exportImportType,
  importError,
  importStock,
  isLoading,
  isModalOpen,
  selectedCompany,
  selectedEntrepotFile,
  selectedEntrepotId,
  setDataImport,
  setExportImportType,
  setImportError,
  setIsLoading,
  setIsModalOpen,
  companyId,
}: any) => {
  const { token } = useAuth();
  const { startJob } = useJobs();
  const classes = useStyles();
  const [isExporting, setIsExporting] = useState(false);

  const handleImportSuccess = async (data: any[], acceptedCsv: any) => {
    setDataImport({ data, acceptedCsv });
  };

  const onValidateModal = async () => {
    setImportError("");
    if (dataImport) {
      setIsLoading(true);
      try {
        await importStock(
          dataImport.data,
          dataImport.acceptedCsv,
          selectedEntrepotId,
          companyId
        );
        setImportError("");
        setDataImport(undefined);
        setIsLoading(false);
        setIsModalOpen(false);
      } catch (err: any) {
        logger.error(err);
        const message = err?.message || "";
        setImportError(
          message === "Bad Request, Invalid file"
            ? IMPORT_CLIENT_INVALID_ERROR_MSG
            : message || IMPORT_CLIENT_ERROR_MSG
        );
        setDataImport(undefined);
        setIsLoading(false);
      }
    }
  };

  const onCancelModal = () => {
    setDataImport(undefined); // clear so next open doesn't reuse previous file
    setIsModalOpen(false);
  };

  const onUnitExport = async () => {
    // After export, switch to import mode for the next step
    setExportImportType("massImport");
  };

  const handleExportClick = async () => {
    setIsExporting(true);
    try {
      await startJob({
        type: JobType.EXPORT_STOCK,
        label: `Export stock ${selectedEntrepotFile?.name ?? ""}`.trim(),
        params: {
          companyId,
          companyName: selectedCompany?.name ?? "",
          warehouseId: selectedEntrepotId,
          fileName: `Liste des stocks ${selectedEntrepotFile?.name ?? ""} ${
            selectedCompany?.name ?? ""
          }`.trim(),
        },
      });
      setExportImportType("massImport");
    } catch (error) {
      console.error("Error preparing export data:", error);
    } finally {
      setIsExporting(false);
    }
  };

  const onDownloadFile = async () => {
    const result = await getFileUrl(token, companyId, selectedEntrepotId);
    if (
      result &&
      result.data &&
      result.data.data &&
      result.data.data.fileDownloadUrl
    ) {
      window.open(result.data.data.fileDownloadUrl, "_blank");
    }
  };
  const onError = () => {
    setImportError("");
  };

  const importedFiles =
    selectedEntrepotFile?.importedFiles?.filter(
      (file) =>
        file.entrepot === selectedEntrepotId && file.company === companyId
    ) || [];

  return (
    <Modal
      open={isModalOpen}
      onCancel={onCancelModal}
      onValidate={onValidateModal}
      className={classes.modal}
      title="État de stock"
      // Weird cast to match Modal props
      step={exportImportType === "entrepotExport" ? 0 : 1}
      textValidate={
        exportImportType === "entrepotExport" ? "Exporter" : "Enregistrer"
      }
      isLoading={isLoading || isExporting}
      onCsvExported={onUnitExport}
      onExportClick={handleExportClick}
      entrepotsSelectedFile={selectedEntrepotFile?.importedFiles || undefined}
    >
      {exportImportType === "entrepotExport" && selectedEntrepotFile && (
        <Text>
          Exporter le stock de l&lsquo;entrepôt{" "}
          <b>{selectedEntrepotFile.name}</b>
        </Text>
      )}
      {exportImportType === "massImport" && (
        <>
          <Import
            key={isModalOpen ? "open" : "closed"}
            className={classes.paperModal}
            onImportSuccess={handleImportSuccess}
            onImportError={onError}
            mainErrorMessage={importError}
          />
          {importedFiles.length > 0 && (
            <Box className={classes.fileDownloader}>
              <Box className={classes.filename}>{importedFiles[0].file}</Box>
              <Box>
                {dateformat(
                  importedFiles[0].date,
                  `${DEFAULT_DATE_FORMAT}" "HH"h"MM`
                )}
              </Box>
              <Button
                onClick={onDownloadFile}
                color="primary"
                startIcon={<Upload2LineIcon />}
              />
            </Box>
          )}
          {!selectedEntrepotFile && companyLastFile?.name && (
            <Box className={classes.fileDownloader}>
              <Box className={classes.filename}>{companyLastFile.name}</Box>
              <Box>
                {dateformat(
                  companyLastFile.updatedAt,
                  `${DEFAULT_DATE_FORMAT}" "HH"h"MM`
                )}
              </Box>
              <Button
                onClick={onDownloadFile}
                startIcon={<Upload2LineIcon />}
              />
            </Box>
          )}
        </>
      )}
    </Modal>
  );
};
